import { useCallback, useEffect, useRef, useState } from "react";
import { getWaitingOnWorkItems } from "../service/inbox";

/**
 * Work-items context for the open document's matter.
 *
 * One fetch feeds three consumers: the verdict bar (`linked` — open
 * items this document already answers), the WaitingOnBanner (`items` —
 * open items expecting something inbound, inbox only), and the inbox
 * action footer's one-door gate (a migrated matter hides the
 * Litify-bound Create Task — the Work Items loop is the one door for
 * accountable work there; unmigrated matters keep Create Task, which
 * still reaches Litify). Failures resolve to null: no banner, no
 * verdict bar, no hiding — the hint is never a gate.
 *
 * Shared by the desktop AIPane (inbox mode), MobileInbox, and the
 * matter document view's DocumentWorkItemBar (Bugbot, PR #636).
 * Responses are sequence-guarded: the reader can flip documents faster
 * than Salesforce answers, and a stale response must never masquerade
 * as the current document's context.
 *
 * @param {string|number|null} documentId — pass null/undefined to
 *   disable (e.g. no document is open).
 * @returns {{ waitingOn: object|null, reload: () => void }}
 */
const useWaitingOnWorkItems = (documentId) => {
  const [waitingOn, setWaitingOn] = useState(null);
  const requestSeq = useRef(0);

  const reload = useCallback(() => {
    const seq = ++requestSeq.current;
    if (documentId == null) {
      setWaitingOn(null);
      return;
    }
    getWaitingOnWorkItems(documentId)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setWaitingOn(response?.data || null);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setWaitingOn(null);
      });
  }, [documentId]);

  useEffect(() => {
    setWaitingOn(null);
    reload();
  }, [reload]);

  return { waitingOn, reload };
};

export default useWaitingOnWorkItems;
